import { useEffect, useRef, useState } from "react";

import {
  DataLayerEvent,
  dreamSearchItemToEcommerceItem,
  ECOMMERCE_EVENT,
  ResultItemForEcommerce,
} from "@portal/app/_common/lib/analytics";
import { useDataLayer } from "@portal/app/_components/hooks/useDataLayer";

export const useDreamSearchImpression = (
  ref: React.RefObject<Element | null>,
  item: ResultItemForEcommerce | undefined,
  index: number
) => {
  const { ready } = useDataLayer();
  const [events, setEvents] = useState<DataLayerEvent[]>([]);

  /**
   * This ensures that we don't track an impression on each re-render. We could
   * still re-track the impression if the component loads again, though. We
   * depend on Google Analytics reporting to be able to dedupe these.
   */
  const impressionWasTracked = useRef(false);

  useEffect(() => {
    /*
    IntersectionObserver is a native function but some older browsers may
    not support it. If this happens, tracking is skipped.
    */
    if (ref.current && item && "IntersectionObserver" in window) {
      const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting && !impressionWasTracked.current) {
          impressionWasTracked.current = true;

          const eventData: DataLayerEvent = {
            event: ECOMMERCE_EVENT.VIEW_ITEM_LIST,
            ecommerce: {
              items: [{ index, ...dreamSearchItemToEcommerceItem(item) }],
            },
          };
          if (ready) {
            window.dataLayer.push(eventData);
          } else {
            setEvents((prevEvents) => [...prevEvents, eventData]);
          }
        }
      });

      observer.observe(ref.current);
      return () => {
        observer.disconnect();
      };
    }
  }, [index, item, ready, ref]);

  useEffect(() => {
    if (ready && events.length) {
      setEvents((prevEvents) => {
        window.dataLayer.push(...prevEvents);
        return [];
      });
    }
  }, [ready, events.length]);
};
